import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { AccountFilterComponent } from "../../../_shared/components";
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { NgbDropdownModule, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '../../../_shared/services/error-handler.service';
import { AccountFilterData, DateIntervalFilterData } from '../../../_shared/models';
import { DateInterval } from '../../../_shared/enums';
import { SearchComponent } from "../../../_shared/components/filters/search/search.component";
import { PaginationComponent } from '../../../_shared/components/pagination/pagination.component';
import { ExportComponent } from '../../../_shared/components/export/export/export.component';
import { AiSupplierOptimizationComponent } from "./components/ai-supplier-optimization/ai-supplier-optimization.component";
import { UpsertSupplierModalComponent } from './components/modals/upsert-supplier-modal/upsert-supplier-modal.component';
import { SuppliersService } from './services/suppliers.service';
import { Supplier, SupplierCategory } from './models/supplier.model';
import { SuppliersListPaginationRequest } from './models/supplier-pagination-request.model';
import { ConfirmationModalService } from '../../../_shared/services/modals/confirmation-modal.service';
import { ConfirmationOptions } from '../../../_shared/models/modals/confirmation-options.model';
import { ToastrService } from 'ngx-toastr';
import { CurrencyHelper } from '../../../_shared/helpers/currency/currency-map.helper';
import { formatCost } from '../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-suppliers',
  standalone: true,
  imports: [CommonModule, AccountFilterComponent, SearchComponent, NgbDropdownModule, PaginationComponent, ExportComponent, AiSupplierOptimizationComponent, TranslocoDirective],
  templateUrl: './suppliers.component.html',
  styleUrl: './suppliers.component.scss'
})
export class SuppliersComponent extends BasePageComponent implements OnInit, OnDestroy {
  private modalService: NgbModal = inject(NgbModal);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private suppliersService: SuppliersService = inject(SuppliersService);
  private confirmationModalService = inject(ConfirmationModalService);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);

  private readonly cancelSubject = new Subject<void>();

  categories: SupplierCategory[] = [];
  suppliers: Supplier[] = [];
  isLoading: boolean = true;
  isCategoriesLoading: boolean = true;
  request: SuppliersListPaginationRequest;
  sortDirection: 'asc' | 'desc' = 'asc';
  sortColumn: keyof Supplier = 'name';

  public searchTerm: string = '';
  public dateFilter: DateIntervalFilterData = {} as DateIntervalFilterData;
  public accountFilter: AccountFilterData = {} as AccountFilterData;
  public defaultInterval: DateInterval = DateInterval.Month;
  public disabledIntervals: DateInterval[] = [DateInterval.Half];
  public selectedCategory: string = this.transloco.translate('suppliers.filters.category');
  public selectedCategoryId?: number;
  public currencySymbol: string;
  public currentPage: number = 1;
  public pageSize: number = 10;
  public totalItems: number = 0;
  public totalPages: number = 0;

  override ngOnInit(): void {
    super.ngOnInit();
    this.getCategories();
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.authService.getUserSettings()?.currencyCode ?? 'ZAR')
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async getCategories(): Promise<void> {
    try {
      const response = await this.suppliersService.getSupplierCategoriesAsync();
      this.categories = response.data ?? [];
    } catch (error: any) {
      this.errorService.handleError(error);
    } finally {
      this.isCategoriesLoading = false;
    }
  }

  private async loadSuppliers(): Promise<void> {
    this.cancelSubject.next();

    try {
      this.isLoading = true;

      this.request = {
        page: this.currentPage,
        pageSize: this.pageSize,
        searchTerm: this.searchTerm || undefined,
        accountIds: this.accountFilter.accountIds ?? [],
        categoryId: this.selectedCategoryId,
        sortDirection: this.sortDirection,
        sortColumn: this.sortColumn
      };

      const response = await this.suppliersService.getSuppliersAsync(this.request, this.cancelSubject);
      this.suppliers = response.data?.results ?? [];
      this.totalItems = response.data?.totalCount ?? 0;
      this.totalPages = response.data?.totalPages ?? 0;
    } catch (error: any) {
      this.errorService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  sortByColumn(column: keyof Supplier) {
    this.currentPage = 1;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    this.loadSuppliers();
  }


  onSearchTermUpdate(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.currentPage = 1;
    this.loadSuppliers();
  }

  onCategoryFilterChange(category: SupplierCategory): void {
    this.selectedCategory = category.name;
    this.selectedCategoryId = category.id;
    this.currentPage = 1;
    this.loadSuppliers();
  }

  onClearCategoryFilter(): void {
    this.selectedCategory = this.transloco.translate('suppliers.filters.allCategories');
    this.selectedCategoryId = undefined;
    this.currentPage = 1;
    this.loadSuppliers();
  }

  async onPageChange(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.loadSuppliers();
  }

  getShowingText(): string {
    const itemsShown = Math.min(this.currentPage * this.pageSize, this.totalItems);
    return this.transloco.translate('common.showingOutOf', { current: itemsShown, total: this.totalItems });
  }

  protected override onRefresh(): void {
    this.loadSuppliers();
  }

  protected override shouldRefresh(): boolean {
    return false;
  }

  protected override getFilters() {
    return {};
  }

  protected override applyPageFilterState(pageFilter: any): void { }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.currentPage = 1;
    this.loadSuppliers();
  }

  openUpsertSupplierModal(supplier?: Supplier): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'supplier-modal-window'
    };

    const modalRef = this.modalService.open(UpsertSupplierModalComponent, modalOptions);

    if (supplier) {
      modalRef.componentInstance.supplier = supplier;
    }

    modalRef.closed.subscribe(() => {
      this.loadSuppliers();
    });
  }

  onEditClick(item: Supplier, event: MouseEvent): void {
    this.openUpsertSupplierModal(item);
  }

  onDeleteClick(item: Supplier, event: MouseEvent): void {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('suppliers.delete.title'),
      message: this.transloco.translate('suppliers.delete.message'),
      warningMessage: this.transloco.translate('suppliers.delete.warning'),
      confirmText: this.transloco.translate('suppliers.delete.confirmText'),
      declineText: this.transloco.translate('suppliers.delete.declineText'),
      confirmPlaceholder: this.transloco.translate('suppliers.delete.confirmPlaceholder'),
      confirmationPrompt: 'delete',
      onConfirm: async () => {
        try {
          await this.suppliersService.deleteSupplierAsync(item.id);
          this.toastr.success(this.transloco.translate('suppliers.toasts.deleted'));
          this.loadSuppliers();
        } catch (error) {
          this.errorService.handleError(error);
        }
      }
    };

    this.confirmationModalService.showConfirmationModal(options);
  }

  getExportRequest(): any {
    return {
      accountIds: this.accountFilter.accountIds ?? [],
      searchTerm: this.searchTerm || undefined,
      categoryId: this.selectedCategoryId,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection
    };
  }

  currencyFormat(cost: number | undefined | null): string | undefined {
    if (cost === undefined || cost === null || isNaN(cost)) return undefined;

    return formatCost(cost, this.currencySymbol);
  }
}